// ExcelCatalogManager - gestione listino materiali via Excel (template, import con validazione, export)
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Download,
  FileSpreadsheet,
  RefreshCw,
  Loader2,
  FileUp,
  FileDown
} from 'lucide-react';
import { ImportStepIndicator } from './excel-catalog/ImportStepIndicator';
import { ImportUploadStep } from './excel-catalog/ImportUploadStep';
import { ImportPreviewStep } from './excel-catalog/ImportPreviewStep';
import { ImportConfirmStep } from './excel-catalog/ImportConfirmStep';
import { ImportResultStep } from './excel-catalog/ImportResultStep';

export default function ExcelCatalogManager({ onImportComplete }) {
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: anteprima, 3: conferma, 4: risultato
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await api.get('/catalog/excel/template', { responseType: 'blob' });
      downloadBlob(response.data, `modello_listino_materiali_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Modello Excel scaricato');
    } catch (error) {
      toast.error('Errore nel download del modello');
      console.error('Errore download modello:', error);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExportCatalog = async () => {
    setExporting(true);
    try {
      const response = await api.get('/catalog/excel/export', { responseType: 'blob' });
      downloadBlob(response.data, `listino_materiali_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Listino esportato con successo');
    } catch (error) {
      toast.error('Errore nell\'export del listino');
      console.error('Errore export:', error);
    } finally {
      setExporting(false);
    }
  };

  const openImportDialog = () => {
    setShowImportDialog(true);
    setImportStep(1);
    setSelectedFile(null);
    setValidationResult(null);
    setSessionId(null);
    setImportResult(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        toast.error('Seleziona un file Excel (.xlsx)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) return;

    setValidating(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/catalog/excel/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setValidationResult(response.data.validation);
      setSessionId(response.data.session_id);
      setImportStep(2);

      if (response.data.validation.error_items > 0) {
        toast.warning(`Trovati ${response.data.validation.error_items} errori da correggere`);
      } else {
        toast.success('Validazione completata');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella validazione');
      console.error('Errore validazione:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!sessionId) return;

    setImporting(true);
    try {
      const response = await api.post('/catalog/excel/import', {
        session_id: sessionId,
        confirm: true
      });

      setImportResult(response.data);
      setImportStep(4);

      if (response.data.status === 'success') {
        toast.success(`Import completato: ${response.data.created} creati, ${response.data.updated} aggiornati`);
      } else {
        toast.warning('Import completato con alcuni errori');
      }

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'import');
      console.error('Errore import:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = async () => {
    if (sessionId) {
      try {
        await api.post('/catalog/excel/import', {
          session_id: sessionId,
          confirm: false
        });
      } catch (e) {
        // ignora errori di annullamento
      }
    }
    setShowImportDialog(false);
  };

  return (
    <>
      {/* Card azioni */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Import / Export Excel</CardTitle>
          </div>
          <CardDescription>
            Gestisci il listino materiali tramite file Excel
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col gap-2"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              data-testid="download-template-btn"
            >
              {downloadingTemplate ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <FileDown className="w-6 h-6 text-blue-600" />
              )}
              <span className="text-xs">Scarica modello</span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col gap-2"
              onClick={openImportDialog}
              data-testid="import-catalog-btn"
            >
              <FileUp className="w-6 h-6 text-emerald-600" />
              <span className="text-xs">Importa articoli</span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col gap-2"
              onClick={handleExportCatalog}
              disabled={exporting}
              data-testid="export-catalog-btn"
            >
              {exporting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Download className="w-6 h-6 text-purple-600" />
              )}
              <span className="text-xs">Esporta listino</span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col gap-2"
              onClick={openImportDialog}
              data-testid="update-catalog-btn"
            >
              <RefreshCw className="w-6 h-6 text-amber-600" />
              <span className="text-xs">Aggiorna da Excel</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog import */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Listino Materiali
            </DialogTitle>
            <DialogDescription>
              {importStep === 1 && 'Carica un file Excel con gli articoli da importare'}
              {importStep === 2 && 'Verifica i dati prima dell\'import'}
              {importStep === 3 && 'Conferma l\'operazione di import'}
              {importStep === 4 && 'Riepilogo operazione'}
            </DialogDescription>
          </DialogHeader>

          <Separator />

          <ImportStepIndicator currentStep={importStep} />

          {importStep === 1 && (
            <ImportUploadStep
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
              onClearFile={() => setSelectedFile(null)}
              onCancel={handleCancelImport}
              onValidate={handleValidate}
              validating={validating}
            />
          )}
          {importStep === 2 && (
            <ImportPreviewStep
              validationResult={validationResult}
              onBack={() => setImportStep(1)}
              onCancel={handleCancelImport}
              onProceed={() => setImportStep(3)}
            />
          )}
          {importStep === 3 && (
            <ImportConfirmStep
              validationResult={validationResult}
              importing={importing}
              onBack={() => setImportStep(2)}
              onCancel={handleCancelImport}
              onConfirm={handleConfirmImport}
            />
          )}
          {importStep === 4 && (
            <ImportResultStep
              importResult={importResult}
              onClose={() => setShowImportDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
